"use client";

import Prompt from "./prompt";
import { useState, useEffect } from "react";
import { useSearchParams } from "next/navigation";
import posthog from "posthog-js";
import SampleImages from "./sample-images";
import ImageUpload from "./image-upload";
import { InferenceType } from "@/lib/playground-utils";

export default function PlaygroundForm({
  inferenceUrl,
  variant,
  sampleImages = [],
  setInferences,
}: {
  inferenceUrl: string;
  variant?: string;
  sampleImages?: string[];
  setInferences: React.Dispatch<React.SetStateAction<InferenceType[]>>;
}) {
  const searchParams = useSearchParams();
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [initialPrompt, setInitialPrompt] = useState<string | null>(null);
  const [initialSkill, setInitialSkill] = useState<string | null>(null);

  const handleImageUpload = (file: File) => {
    if (file.type.startsWith("image/")) {
      // Reset result state when a new image is uploaded
      setUploadedImage(null);
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageRemove = () => {
    setUploadedImage(null);
    setImageFile(null);
  };

  const handleSampleImageSelect = (imageSrc: string) => {
    // Fetch the image from the public folder
    fetch(imageSrc)
      .then((response) => response.blob())
      .then((blob) => {
        // Extract filename from the path for better identification
        const filename = imageSrc.split('/').pop() || "sample-image.jpg";
        const fileExtension = filename.split('.').pop() || "jpg";
        const mimeType = fileExtension === "png" ? "image/png" : "image/jpeg";
        
        // Create a file from the blob with the original filename
        const file = new File([blob], filename, {
          type: mimeType,
        });
        handleImageUpload(file);
      })
      .catch((error) => {
        posthog.captureException(error);
      });
  };

  // Load initial image, prompt, and skill from URL parameters
  useEffect(() => {
    const imageParam = searchParams.get('image');
    const promptParam = searchParams.get('prompt');
    const skillParam = searchParams.get('skill');
    
    if (imageParam && !uploadedImage) {
      handleSampleImageSelect(imageParam);
    }
    
    if (promptParam && !initialPrompt) {
      setInitialPrompt(promptParam);
    }
    
    if (skillParam && !initialSkill) {
      setInitialSkill(skillParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Prevent default form submission
  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
  };

  return (
    <form
      id="playground-form"
      className="w-full flex flex-col gap-4"
      onSubmit={handleFormSubmit}
    >
      {sampleImages.length > 0 && (
        <SampleImages 
          images={sampleImages}
          onImageSelect={handleSampleImageSelect} 
        />
      )}
      <div className="grid gap-4 md:grid-cols-3 md:grid-rows-1">
        <div className="md:col-span-1 h-full min-h-[200px]">
          <ImageUpload
            uploadedImage={uploadedImage}
            onImageUpload={handleImageUpload}
            onImageRemove={handleImageRemove}
          />
        </div>
        <div className="md:col-span-2 h-full min-h-[200px]">
          <Prompt
            inferenceUrl={inferenceUrl}
            variant={variant}
            imageFile={imageFile}
            uploadedImage={uploadedImage}
            setInferences={setInferences}
            initialPrompt={initialPrompt}
            initialSkill={initialSkill}
          />
        </div>
      </div>
    </form>
  );
}
